import { useEffect, useMemo, useState } from "react"

import {
  usePixels,
  useAdminUsers,
  usePixelDetails,
  useValidatePixel,
  useCreatePixel,
  useUpdatePixel,
  useDeletePixel,
  type Pixel,
} from "@/api/hooks/admin"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"

function showToast(msg: string, type: "success" | "error" = "success") {
  const el = document.createElement("div")
  el.textContent = msg
  el.className = `fixed top-4 right-4 z-[9999] rounded-lg px-4 py-3 text-sm font-medium shadow-lg ${
    type === "error"
      ? "bg-red-900/90 text-red-200 border border-red-700"
      : "bg-green-900/90 text-green-200 border border-green-700"
  }`
  document.body.appendChild(el)
  setTimeout(() => el.remove(), 5000)
}

export default function TikTokPixelsTab({
  search = "",
  registerOpenCreate,
}: {
  search?: string
  registerOpenCreate?: (fn: (() => void) | null) => void
}) {
  const { data, isLoading, isError } = usePixels()
  const { data: usersData } = useAdminUsers()
  const validatePixel = useValidatePixel()
  const createPixel = useCreatePixel()
  const updatePixel = useUpdatePixel()
  const deletePixel = useDeletePixel()

  const [modalOpen, setModalOpen] = useState(false)
  const [editPixelId, setEditPixelId] = useState<number | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Pixel | null>(null)

  const [formName, setFormName] = useState("")
  const [formPixelId, setFormPixelId] = useState("")
  const [formAccessToken, setFormAccessToken] = useState("")
  const [formUserId, setFormUserId] = useState<number | "">("")
  const [saving, setSaving] = useState(false)

  const pixels: Pixel[] = data?.data ?? []
  const allUsers = usersData?.data ?? []
  const filteredPixels = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return pixels
    return pixels.filter((p) => {
      const haystack = [p.name, p.pixel_id, p.owner_username, String(p.id)]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
      return haystack.includes(q)
    })
  }, [pixels, search])

  const { data: detailsData } = usePixelDetails(editPixelId)

  function openCreateModal() {
    setEditPixelId(null)
    setFormName("")
    setFormPixelId("")
    setFormAccessToken("")
    setFormUserId("")
    setModalOpen(true)
  }

  useEffect(() => {
    registerOpenCreate?.(openCreateModal)
    return () => registerOpenCreate?.(null)
  }, [registerOpenCreate])

  function openEditModal(pixel: Pixel) {
    setEditPixelId(pixel.id)
    setFormName(pixel.name)
    setFormPixelId(pixel.pixel_id)
    setFormAccessToken("")
    setFormUserId(pixel.user_id)
    setModalOpen(true)
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    if (!formName || !formPixelId || formUserId === "") return
    setSaving(true)

    try {
      const tokenToValidate =
        formAccessToken || (editPixelId ? detailsData?.data?.access_token : "")
      if (tokenToValidate) {
        const validation = await validatePixel.mutateAsync({
          pixel_id: formPixelId,
          access_token: tokenToValidate,
        })
        if (!validation.valid) {
          showToast(validation.error ?? "Pixel validation failed", "error")
          setSaving(false)
          return
        }
      }

      if (editPixelId) {
        await updatePixel.mutateAsync({
          id: editPixelId,
          name: formName,
          pixel_id_value: formPixelId,
          access_token: formAccessToken,
          user_id: Number(formUserId),
        })
        showToast("Pixel updated successfully")
      } else {
        if (!formAccessToken) {
          showToast("Access token is required for new pixels", "error")
          setSaving(false)
          return
        }
        await createPixel.mutateAsync({
          name: formName,
          pixel_id: formPixelId,
          access_token: formAccessToken,
          user_id: Number(formUserId),
        })
        showToast("Pixel created successfully")
      }
      setModalOpen(false)
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : "Error saving pixel",
        "error",
      )
    } finally {
      setSaving(false)
    }
  }

  function handleDelete(pixel: Pixel) {
    // In-app confirm — browser window.confirm follows OS locale ("Скасувати").
    setDeleteTarget(pixel)
  }

  function confirmDelete() {
    if (!deleteTarget) return
    const pixel = deleteTarget
    deletePixel.mutate(pixel.id, {
      onSuccess: (res) => {
        setDeleteTarget(null)
        showToast(res?.message || "Pixel deleted successfully")
      },
      onError: (err) =>
        showToast(
          err instanceof Error ? err.message : "Error deleting pixel",
          "error",
        ),
    })
  }

  if (isLoading) {
    return (
      <div className="py-8 text-center text-white">Loading pixels...</div>
    )
  }

  if (isError) {
    return (
      <div className="py-8 text-center text-red-400">Error loading pixels</div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-4 overflow-hidden">
      {pixels.length === 0 ? (
        <div className="py-8 text-center text-white">
          No pixels found. Create one to get started.
        </div>
      ) : filteredPixels.length === 0 ? (
        <div className="py-8 text-center text-white">
          No pixels match “{search.trim()}”
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-[#4a4a4a]">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="sticky top-0 z-10 bg-[#444444] text-white">
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Pixel ID</th>
                <th className="px-4 py-3 font-semibold">Owner</th>
                <th className="px-4 py-3 font-semibold">Created</th>
                <th className="px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPixels.map((pixel) => (
                <tr
                  key={pixel.id}
                  className="border-b border-[#4a4a4a] transition-colors hover:bg-[#444444]"
                >
                  <td className="px-4 py-3 font-medium text-white">
                    {pixel.name}
                  </td>
                  <td className="px-4 py-3">
                    <code className="rounded bg-[#2d2d2d] px-1.5 py-0.5 text-xs text-white">
                      {pixel.pixel_id}
                    </code>
                  </td>
                  <td className="px-4 py-3 text-white">
                    {pixel.owner_username}
                  </td>
                  <td className="px-4 py-3 text-white">
                    {pixel.created_at
                      ? new Date(pixel.created_at).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5">
                      <button
                        className="rounded-md border border-[#8fa0ef]/35 bg-[#7488e8] px-2.5 py-1 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#687bda]"
                        onClick={() => openEditModal(pixel)}
                      >
                        Edit
                      </button>
                      <button
                        className="rounded-md border border-[#d26a78]/35 bg-[#c6596a] px-2.5 py-1 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#b84f60]"
                        onClick={() => handleDelete(pixel)}
                        disabled={deletePixel.isPending}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog
        open={deleteTarget != null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <DialogContent className="border-[#4a4a4a] bg-[#3a3a3a] text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete pixel</DialogTitle>
            <DialogDescription className="text-white/80">
              Are you sure you want to delete pixel &apos;{deleteTarget?.name}
              &apos;? This cannot be undone. Offers using it will have their
              pixel link cleared and Active offers will move to Attention.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => setDeleteTarget(null)}
              disabled={deletePixel.isPending}
              className="flex-1 rounded-md border border-[#7a808b]/45 bg-[#636b78] px-4 py-2 font-semibold text-white shadow-sm transition-colors hover:bg-[#58606c] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmDelete}
              disabled={deletePixel.isPending}
              className="flex-1 rounded-md border border-[#d26a78]/35 bg-[#c6596a] px-4 py-2 font-semibold text-white shadow-sm transition-colors hover:bg-[#b84f60] disabled:opacity-50"
            >
              {deletePixel.isPending ? "Deleting…" : "Delete"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={modalOpen} onOpenChange={(open) => !open && setModalOpen(false)}>
        <DialogContent className="border-[#4a4a4a] bg-[#3a3a3a] text-white">
          <DialogHeader>
            <DialogTitle>
              {editPixelId ? "Edit Pixel" : "Create Pixel"}
            </DialogTitle>
            <DialogDescription className="text-white">
              {editPixelId
                ? "Update the pixel details below."
                : "Fill in the details to create a new pixel."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium">Name</label>
              <input
                type="text"
                required
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                className="w-full rounded-md border border-[#555] bg-[#2d2d2d] px-3 py-2 text-white"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Pixel ID</label>
              <input
                type="text"
                required
                value={formPixelId}
                onChange={(e) => setFormPixelId(e.target.value)}
                className="w-full rounded-md border border-[#555] bg-[#2d2d2d] px-3 py-2 text-white"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">
                Access Token{" "}
                {editPixelId && (
                  <span className="font-normal text-white">
                    (leave empty to keep existing)
                  </span>
                )}
              </label>
              <input
                type="text"
                required={!editPixelId}
                value={formAccessToken}
                onChange={(e) => setFormAccessToken(e.target.value)}
                className="w-full rounded-md border border-[#555] bg-[#2d2d2d] px-3 py-2 text-white"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">
                Owner (User)
              </label>
              <select
                required
                value={formUserId}
                onChange={(e) =>
                  setFormUserId(e.target.value ? Number(e.target.value) : "")
                }
                className="w-full rounded-md border border-[#555] bg-[#2d2d2d] px-3 py-2 text-white"
              >
                <option value="">Select user...</option>
                {allUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.username} ({u.role})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 rounded-md border border-[#8fa0ef]/35 bg-[#7488e8] px-4 py-2 font-semibold text-white shadow-sm transition-colors hover:bg-[#687bda] disabled:opacity-50"
              >
                {saving
                  ? validatePixel.isPending
                    ? "Validating..."
                    : "Saving..."
                  : "Save Pixel"}
              </button>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="flex-1 rounded-md border border-[#7a808b]/45 bg-[#636b78] px-4 py-2 font-semibold text-white shadow-sm transition-colors hover:bg-[#58606c]"
              >
                Cancel
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
